import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Save, Shield, AlertTriangle, Ban, Unlock, Eye, X, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import type { SettingsTabProps } from './types';
import SecureInput from '@/components/admin/SecureInput';
import { validateIPAddress, sanitizeInput } from '@/utils/securityUtils';

const SecuritySettingsTab: React.FC<SettingsTabProps> = ({ settings, onSettingsChange }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    settings.maintenance_mode_message || "We are currently performing scheduled maintenance. Please check back soon."
  );
  const [newBlockIP, setNewBlockIP] = useState('');
  const [newBlockReason, setNewBlockReason] = useState('');
  const [blockDuration, setBlockDuration] = useState('24');
  const [isPermanent, setIsPermanent] = useState(false);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [selectedIPs, setSelectedIPs] = useState<Set<string>>(new Set());
  
  // Pagination states for audit logs
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);
  const [selectedAuditLogs, setSelectedAuditLogs] = useState<Set<string>>(new Set());
  
  const queryClient = useQueryClient();

  // Fetch additional security settings
  const { data: additionalSecuritySettings } = useQuery({
    queryKey: ['security-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_settings')
        .select('key, value')
        .in('key', ['auto_block_admin_access', 'maintenance_mode_message']);
      
      if (error) throw error;
      
      return (data || []).reduce((acc: Record<string, any>, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {});
    },
  });

  // Fetch blocked IPs with more details
  const { data: blockedIPs, refetch: refetchBlockedIPs } = useQuery({
    queryKey: ['blocked-ips'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blocked_ips')
        .select('*')
        .order('blocked_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch recent attempts
  const { data: recentAttempts } = useQuery({
    queryKey: ['recent-unauthorized-attempts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('security_audit_logs')
        .select('*')
        .eq('event_type', 'UNAUTHORIZED_ADMIN_ACCESS')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      
      // Group by IP and get latest attempt for each
      const ipMap = new Map();
      data?.forEach(log => {
        if (log.ip_address && !ipMap.has(log.ip_address)) {
          const eventDetails = log.event_details as any;
          ipMap.set(log.ip_address, {
            ip_address: log.ip_address,
            latest_attempt: log.created_at,
            path: eventDetails?.path || 'Unknown',
            user_agent: log.user_agent,
            attempt_count: data.filter(l => l.ip_address === log.ip_address).length
          });
        }
      });
      
      return Array.from(ipMap.values());
    },
  });

  // Fetch security audit logs with pagination
  const { data: securityAuditData, isLoading: isLoadingAuditLogs } = useQuery({
    queryKey: ['security-audit-logs', auditPage, auditPageSize],
    queryFn: async () => {
      const from = (auditPage - 1) * auditPageSize;
      const to = from + auditPageSize - 1;
      
      const { data, error, count } = await supabase
        .from('security_audit_logs')
        .select('*', { count: 'exact' })
        .eq('event_type', 'UNAUTHORIZED_ADMIN_ACCESS')
        .order('created_at', { ascending: false })
        .range(from, to);
      
      if (error) throw error;
      return { logs: data || [], totalCount: count || 0 };
    },
  });

  // Enhanced settings mutation with additional security settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);
          
        const { error } = await supabase
          .from('website_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `Setting for ${key}` 
          }, {
            onConflict: 'key'
          });
          
        if (error) {
          throw error;
        }
      });
      
      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-settings'] });
      queryClient.invalidateQueries({ queryKey: ['security-settings'] });
      queryClient.refetchQueries({ queryKey: ['website-settings'] });
      toast.success('Security settings updated successfully');
      setErrors({});
    },
    onError: (error: any) => {
      toast.error('Failed to update security settings');
      setErrors({ security: error.message || 'Failed to save settings' });
    },
  });

  // Enhanced block IP mutation with validation
  const blockIPMutation = useMutation({
    mutationFn: async ({ ips, reason, hours, permanent }: { ips: string[], reason: string, hours?: number, permanent: boolean }) => {
      // Validate IPs
      const invalidIPs = ips.filter(ip => !validateIPAddress(ip));
      if (invalidIPs.length > 0) {
        throw new Error(`Invalid IP addresses: ${invalidIPs.join(', ')}`);
      }

      // Sanitize reason
      const sanitizedReason = sanitizeInput(reason, 500);
      if (!sanitizedReason.trim()) {
        throw new Error('Block reason is required');
      }

      const promises = ips.map(ip => 
        supabase.rpc('block_ip', {
          p_ip_address: ip,
          p_reason: sanitizedReason,
          p_duration_hours: permanent ? null : hours,
          p_is_permanent: permanent
        })
      );
      
      const results = await Promise.allSettled(promises);
      const failures = results.filter(r => r.status === 'rejected');
      
      if (failures.length > 0) {
        throw new Error(`Failed to block ${failures.length} IP(s)`);
      }
    },
    onSuccess: (_, variables) => {
      const count = variables.ips.length;
      toast.success(`Successfully blocked ${count} IP address${count > 1 ? 'es' : ''}`);
      refetchBlockedIPs();
      queryClient.invalidateQueries({ queryKey: ['recent-unauthorized-attempts'] });
      setIsBlockDialogOpen(false);
      setNewBlockIP('');
      setNewBlockReason('');
      setBlockDuration('24');
      setIsPermanent(false);
      setSelectedIPs(new Set());
    },
    onError: (error: any) => {
      toast.error('Failed to block IP address(es)');
      console.error('Block IP error:', error);
    },
  });

  // Unblock IP mutation
  const unblockIPMutation = useMutation({
    mutationFn: async (ipId: string) => {
      const { error } = await supabase
        .from('blocked_ips')
        .delete()
        .eq('id', ipId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('IP address unblocked successfully');
      refetchBlockedIPs();
    },
    onError: (error: any) => {
      toast.error('Failed to unblock IP address');
      console.error('Unblock IP error:', error);
    },
  });

  // Bulk delete audit logs mutation
  const bulkDeleteAuditLogsMutation = useMutation({
    mutationFn: async (logIds: string[]) => {
      const { error } = await supabase
        .from('security_audit_logs')
        .delete()
        .in('id', logIds);
      
      if (error) throw error;
    },
    onSuccess: (_, logIds) => {
      toast.success(`Successfully deleted ${logIds.length} audit log${logIds.length > 1 ? 's' : ''}`);
      queryClient.invalidateQueries({ queryKey: ['security-audit-logs'] });
      setSelectedAuditLogs(new Set());
    },
    onError: (error: any) => {
      toast.error('Failed to delete audit logs');
      console.error('Bulk delete audit logs error:', error);
    },
  });

  const handleSave = () => {
    const sanitizedMessage = sanitizeInput(maintenanceMessage, 2000);
    
    const securitySettingsToUpdate = {
      allow_registration: settings.allow_registration,
      maintenance_mode: settings.maintenance_mode,
      maintenance_mode_message: sanitizedMessage,
      auto_block_admin_access: additionalSecuritySettings?.auto_block_admin_access ?? true,
      geo_access_enabled: additionalSecuritySettings?.geo_access_enabled ?? false,
      allowed_countries: additionalSecuritySettings?.allowed_countries ?? [],
      blocked_countries: additionalSecuritySettings?.blocked_countries ?? [],
      admin_access_attempt_limit: additionalSecuritySettings?.admin_access_attempt_limit ?? 5,
    };

    updateSettingsMutation.mutate(securitySettingsToUpdate);
  };

  const handleBlockIP = () => {
    const ipsToBlock = newBlockIP ? [newBlockIP] : Array.from(selectedIPs);
    
    if (ipsToBlock.length === 0 || !newBlockReason.trim()) {
      toast.error('Please enter reason and select IPs to block');
      return;
    }

    blockIPMutation.mutate({
      ips: ipsToBlock,
      reason: newBlockReason,
      hours: isPermanent ? undefined : parseInt(blockDuration),
      permanent: isPermanent
    });
  };

  const handleSelectIP = (ip: string, checked: boolean) => {
    const newSelected = new Set(selectedIPs);
    if (checked) {
      newSelected.add(ip);
    } else {
      newSelected.delete(ip);
    }
    setSelectedIPs(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && recentAttempts) {
      // Only select IPs that aren't already blocked
      const availableIPs = recentAttempts
        .filter(attempt => !blockedIPs?.some(blocked => blocked.ip_address === attempt.ip_address))
        .map(attempt => attempt.ip_address);
      setSelectedIPs(new Set(availableIPs));
    } else {
      setSelectedIPs(new Set());
    }
  };

  const handleSelectAuditLog = (logId: string, checked: boolean) => {
    const newSelected = new Set(selectedAuditLogs);
    if (checked) {
      newSelected.add(logId);
    } else {
      newSelected.delete(logId);
    }
    setSelectedAuditLogs(newSelected);
  };

  const handleSelectAllAuditLogs = (checked: boolean) => {
    if (checked && securityAuditData?.logs) {
      const allLogIds = securityAuditData.logs.map(log => log.id);
      setSelectedAuditLogs(new Set(allLogIds));
    } else {
      setSelectedAuditLogs(new Set());
    }
  };

  const handleBulkDeleteAuditLogs = () => {
    if (selectedAuditLogs.size === 0) {
      toast.error('Please select audit logs to delete');
      return;
    }
    
    bulkDeleteAuditLogsMutation.mutate(Array.from(selectedAuditLogs));
  };

  const isIPBlocked = (ip: string) => {
    return blockedIPs?.some(blocked => blocked.ip_address === ip);
  };

  const totalPages = securityAuditData ? Math.ceil(securityAuditData.totalCount / auditPageSize) : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Enhanced Security Settings</CardTitle>
          <CardDescription>Configure security and access control settings with enhanced protection</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {errors.security && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {errors.security}
            </div>
          )}

          {/* Enhanced security notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">🔒 Enhanced Security Features Active</h4>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• XSS protection with content sanitization</li>
              <li>• SQL injection prevention</li>
              <li>• Enhanced input validation and rate limiting</li>
              <li>• Comprehensive security audit logging</li>
              <li>• Session security with timeout validation</li>
              <li>• IP-based access control with validation</li>
            </ul>
          </div>

          {/* Existing security settings with SecureInput */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="security_maintenance_mode">Maintenance Mode</Label>
              <p className="text-sm text-gray-600">
                Enable to restrict site access and show maintenance page to visitors
              </p>
            </div>
            <Switch
              id="security_maintenance_mode"
              checked={settings.maintenance_mode || false}
              onCheckedChange={(checked) => onSettingsChange('maintenance_mode', checked)}
            />
          </div>

          {settings.maintenance_mode && (
            <SecureInput
              id="maintenance_message"
              label="Maintenance Mode Message"
              value={maintenanceMessage}
              onChange={setMaintenanceMessage}
              type="textarea"
              placeholder="Enter the message to display during maintenance"
              maxLength={2000}
              preventSQLInjection={true}
            />
          )}

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="security_allow_registration">Allow User Registration</Label>
              <p className="text-sm text-gray-600">
                Allow new users to create accounts on your website
              </p>
            </div>
            <Switch
              id="security_allow_registration"
              checked={settings.allow_registration || false}
              onCheckedChange={(checked) => onSettingsChange('allow_registration', checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="auto_block_admin_access">Auto-Block Admin Access Attempts</Label>
              <p className="text-sm text-gray-600">
                Automatically block IPs that repeatedly try to access admin paths without authorization
              </p>
            </div>
            <Switch
              id="auto_block_admin_access"
              checked={additionalSecuritySettings?.auto_block_admin_access ?? true}
              onCheckedChange={(checked) => {
                updateSettingsMutation.mutate({ auto_block_admin_access: checked });
              }}
            />
          </div>

          {/* New Geographic Access Control Section */}
          <div className="p-4 border rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="geo_access_enabled">Geographic Access Control</Label>
                <p className="text-sm text-gray-600">
                  Restrict access based on visitor's geographic location
                </p>
              </div>
              <Switch
                id="geo_access_enabled"
                checked={additionalSecuritySettings?.geo_access_enabled ?? false}
                onCheckedChange={(checked) => {
                  updateSettingsMutation.mutate({ geo_access_enabled: checked });
                }}
              />
            </div>
            
            {additionalSecuritySettings?.geo_access_enabled && (
              <div className="pl-4 space-y-3 border-l-2 border-blue-100">
                <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
                  <strong>Note:</strong> Geographic restrictions are currently configured for development. 
                  Contact your administrator to configure country restrictions for production use.
                </div>
              </div>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-medium text-amber-900 mb-2">Enhanced Security Features</h4>
            <ul className="text-amber-700 text-sm space-y-1">
              <li>• Enhanced IP-based access control with validation</li>
              <li>• Server-side rate limiting for admin access attempts</li>
              <li>• Improved session security with multi-layer validation</li>
              <li>• Geographic access control (configurable)</li>
              <li>• Enhanced security audit logging with better IP parsing</li>
              <li>• Robust input validation and sanitization</li>
            </ul>
          </div>

          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSave} 
              disabled={updateSettingsMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Security Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced IP Management Section with SecureInput */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Enhanced IP Address Management
            </div>
            <Dialog open={isBlockDialogOpen} onOpenChange={setIsBlockDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Ban className="w-4 h-4 mr-2" />
                  Block IP
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Block IP Addresses</DialogTitle>
                  <DialogDescription>
                    Block IP addresses with enhanced validation and security
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Manual IP Input with validation */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h4 className="font-medium">Manual IP Block</h4>
                    <SecureInput
                      id="block_ip"
                      label="IP Address"
                      value={newBlockIP}
                      onChange={setNewBlockIP}
                      placeholder="192.168.1.1 or 2001:db8::1"
                      preventSQLInjection={true}
                    />
                  </div>

                  {/* Recent Attempts Selection */}
                  {recentAttempts && recentAttempts.length > 0 && (
                    <div className="space-y-4 p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Recent Unauthorized Access Attempts</h4>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="select-all"
                            checked={selectedIPs.size > 0 && selectedIPs.size === recentAttempts.filter(a => !isIPBlocked(a.ip_address)).length}
                            onCheckedChange={handleSelectAll}
                          />
                          <Label htmlFor="select-all">Select All Available</Label>
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">Select</TableHead>
                              <TableHead>IP Address</TableHead>
                              <TableHead>Attempts</TableHead>
                              <TableHead>Latest Path</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {recentAttempts.map((attempt: any) => {
                              const blocked = isIPBlocked(attempt.ip_address);
                              return (
                                <TableRow key={attempt.ip_address}>
                                  <TableCell>
                                    {!blocked && (
                                      <Checkbox
                                        checked={selectedIPs.has(attempt.ip_address)}
                                        onCheckedChange={(checked) => handleSelectIP(attempt.ip_address, checked as boolean)}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">{attempt.ip_address}</TableCell>
                                  <TableCell>{attempt.attempt_count}</TableCell>
                                  <TableCell className="font-mono text-sm">{attempt.path}</TableCell>
                                  <TableCell>
                                    {blocked ? (
                                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                                        Blocked
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                        Active
                                      </span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                      {selectedIPs.size > 0 && (
                        <div className="text-sm text-blue-600">
                          {selectedIPs.size} IP address{selectedIPs.size > 1 ? 'es' : ''} selected for blocking
                        </div>
                      )}
                    </div>
                  )}

                  {/* Block Configuration with SecureInput */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h4 className="font-medium">Block Configuration</h4>
                    <SecureInput
                      id="block_reason"
                      label="Reason"
                      value={newBlockReason}
                      onChange={setNewBlockReason}
                      placeholder="Suspicious activity, spam, etc."
                      maxLength={500}
                      required={true}
                      preventSQLInjection={true}
                    />
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="permanent_block"
                        checked={isPermanent}
                        onCheckedChange={setIsPermanent}
                      />
                      <Label htmlFor="permanent_block">Permanent block</Label>
                    </div>
                    {!isPermanent && (
                      <div>
                        <Label htmlFor="block_duration">Duration (hours)</Label>
                        <Input
                          id="block_duration"
                          type="number"
                          value={blockDuration}
                          onChange={(e) => setBlockDuration(e.target.value)}
                          min="1"
                          max="8760"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsBlockDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBlockIP}
                    disabled={blockIPMutation.isPending}
                  >
                    {blockIPMutation.isPending ? 'Blocking...' : `Block ${(newBlockIP ? 1 : selectedIPs.size)} IP${(newBlockIP ? '' : selectedIPs.size > 1 ? 's' : '')}`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardTitle>
          <CardDescription>
            Manage blocked IP addresses with enhanced validation and security audit logging
          </CardDescription>
        </CardHeader>
        <CardContent>
          {blockedIPs && blockedIPs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Blocked At</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedIPs.map((blockedIP: any) => (
                  <TableRow key={blockedIP.id}>
                    <TableCell className="font-medium">{blockedIP.ip_address}</TableCell>
                    <TableCell className="max-w-xs truncate">{blockedIP.reason}</TableCell>
                    <TableCell>
                      {blockedIP.is_permanent ? (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          Permanent
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          Temporary
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(blockedIP.blocked_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {blockedIP.blocked_until 
                        ? new Date(blockedIP.blocked_until).toLocaleString()
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => unblockIPMutation.mutate(blockedIP.id)}
                        disabled={unblockIPMutation.isPending}
                      >
                        <Unlock className="w-4 h-4 mr-1" />
                        Unblock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-gray-500 text-center py-4">No blocked IP addresses</p>
          )}
        </CardContent>
      </Card>

      {/* Security Audit Logs with Pagination */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Recent Unauthorized Access Attempts
            </div>
            <div className="flex items-center gap-2">
              {selectedAuditLogs.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDeleteAuditLogs}
                  disabled={bulkDeleteAuditLogsMutation.isPending}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedAuditLogs.size})
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Label htmlFor="audit-page-size" className="text-sm whitespace-nowrap">
                  Per page:
                </Label>
                <Select
                  value={auditPageSize.toString()}
                  onValueChange={(value) => {
                    setAuditPageSize(parseInt(value));
                    setAuditPage(1);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardTitle>
          <CardDescription>
            View recent attempts to access admin areas and the paths they tried to visit
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingAuditLogs ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : securityAuditData && securityAuditData.logs.length > 0 ? (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedAuditLogs.size > 0 && selectedAuditLogs.size === securityAuditData.logs.length}
                          onCheckedChange={handleSelectAllAuditLogs}
                        />
                      </TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead className="hidden sm:table-cell">Attempted Path</TableHead>
                      <TableHead className="hidden md:table-cell">User Agent</TableHead>
                      <TableHead className="hidden lg:table-cell">Blocked</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {securityAuditData.logs.map((log: any) => {
                      const eventDetails = log.event_details as any;
                      return (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedAuditLogs.has(log.id)}
                              onCheckedChange={(checked) => handleSelectAuditLog(log.id, checked as boolean)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{log.ip_address}</TableCell>
                          <TableCell className="hidden sm:table-cell font-mono text-sm">
                            {eventDetails?.path || 'Unknown'}
                          </TableCell>
                          <TableCell className="hidden md:table-cell max-w-xs truncate text-sm">
                            {log.user_agent || 'Unknown'}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {eventDetails?.blocked ? (
                              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                                Yes
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                No
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            <div className="flex flex-col">
                              <span>{new Date(log.created_at).toLocaleString()}</span>
                              {/* Show additional info on mobile */}
                              <div className="sm:hidden text-xs text-gray-400 mt-1">
                                <div>Path: {eventDetails?.path || 'Unknown'}</div>
                                <div>
                                  {eventDetails?.blocked ? (
                                    <span className="text-red-600">Blocked</span>
                                  ) : (
                                    <span className="text-green-600">Not Blocked</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-2">
                  <div className="text-sm text-gray-700">
                    Showing {((auditPage - 1) * auditPageSize) + 1} to {Math.min(auditPage * auditPageSize, securityAuditData.totalCount)} of {securityAuditData.totalCount} entries
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAuditPage(Math.max(1, auditPage - 1))}
                      disabled={auditPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Previous</span>
                    </Button>
                    
                    <div className="flex items-center space-x-1">
                      {/* Show page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (auditPage <= 3) {
                          pageNum = i + 1;
                        } else if (auditPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = auditPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === auditPage ? "default" : "outline"}
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => setAuditPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAuditPage(Math.min(totalPages, auditPage + 1))}
                      disabled={auditPage === totalPages}
                    >
                      <span className="hidden sm:inline mr-1">Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No recent unauthorized access attempts</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettingsTab;
